import { Button, CircularProgress, Tooltip, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TAX_REPORT_AUDIT } from 'adminConstants/routes';
import { AppSelectBox, AppTextInput } from 'components/FormElements';
import GridContainer from 'components/GridContainer';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Table from 'components/Table';
import { useGetInvestorsForTaxYear } from 'hooks/data/taxReport/useGetInvestorsForTaxYear';
import {
  useBulkDownloadTaxReports,
  BULK_DOWNLOAD_LIMIT,
} from 'hooks/data/taxReport/useBulkDownloadTaxReports';
import { usePagination } from 'further-ui/hooks';
import { SortOrder } from 'further-types/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import DownloadTaxReportButton from './DownloadTaxReportButton';

const GridMargin = styled(GridContainer)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const DownloadBtn = styled(Grid)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}));

const TaxReportPdfDownload: React.FC = () => {
  const { taxYearStart, firmId } = useParams<{
    taxYearStart: string;
    firmId: string;
  }>();
  const [search, setSearch] = useState('');
  const [isHardCopyOnly, setIsHardCopyOnly] = useState(false);
  const pagination = usePagination({
    order: SortOrder.Asc,
    orderBy: 'fullName',
    pageSize: 25,
  });
  const debouncedSearchTerm = useDebounce(search, 300);

  const { data, isFetching } = useGetInvestorsForTaxYear({
    taxYearStart,
    firmId,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
    search: debouncedSearchTerm || undefined,
    isHardCopyOnly,
  });

  const total = data?.total ?? 0;

  const bulkDownload = useBulkDownloadTaxReports();

  useEffect(() => {
    pagination.toFirstPage();
  }, [debouncedSearchTerm, isHardCopyOnly]);

  const isDownloadAllDisabled =
    total === 0 || total > BULK_DOWNLOAD_LIMIT || bulkDownload.isPending;

  const columns = useMemo(
    () => [
      {
        label: 'Name',
        key: 'fullName',
      },
      {
        label: 'Email',
        key: 'email',
      },
      {
        label: 'External Investor ID',
        key: 'externalInvestorId',
      },
      {
        label: 'Download',
        key: 'download',
        sort: false,
        render: (elm: { _id: string }) => (
          <DownloadTaxReportButton
            taxYearStart={Number(taxYearStart)}
            investorId={elm._id}
          />
        ),
      },
    ],
    [taxYearStart],
  );

  const investorOptions = [
    {
      key: 'false',
      label: 'All investors',
    },
    {
      key: 'true',
      label: 'Investors requiring hard copies',
    },
  ];

  return (
    <PageContainer
      heading="Tax Report Audit: Download PDFs"
      breadcrumbs={[
        { label: 'Dashboard', link: '/' },
        { label: 'Investor Reporting' },
        { label: 'Tax report audit', link: TAX_REPORT_AUDIT },
        { label: 'Download PDFs', isActive: true, withoutFormatting: true },
      ]}
    >
      <PageContent>
        <GridMargin>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <AppTextInput
              name="searchInvestors"
              variant="outlined"
              placeholder="Filter by name, email or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <AppSelectBox
              data={investorOptions}
              valueKey="key"
              labelKey="label"
              value={String(isHardCopyOnly)}
              variant="outlined"
              onChange={(event) => {
                setIsHardCopyOnly(event.target.value === 'true');
              }}
            />
          </Grid>

          <DownloadBtn
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <Tooltip
              title={
                total > BULK_DOWNLOAD_LIMIT ? (
                  <>
                    <strong>Download all</strong> is available for searches of{' '}
                    {BULK_DOWNLOAD_LIMIT} investors or fewer.
                  </>
                ) : (
                  ''
                )
              }
            >
              <span>
                <Button
                  onClick={() =>
                    bulkDownload.mutate({
                      taxYearStart: Number(taxYearStart),
                      firmId,
                      search: debouncedSearchTerm || undefined,
                      isHardCopyOnly,
                    })
                  }
                  disabled={isDownloadAllDisabled}
                  color="primary"
                  variant="contained"
                  startIcon={
                    bulkDownload.isPending ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }
                >
                  Download all
                </Button>
              </span>
            </Tooltip>
          </DownloadBtn>
        </GridMargin>

        <Table
          pagination={true}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={data?.total ?? 0}
          columns={columns}
          tablebody={data?.investors ?? []}
          variant="nohover"
          emptyMessage="No investors were included in this tax year."
          loading={isFetching}
          order={pagination.order}
          orderBy={pagination.orderBy}
          onRequestSort={pagination.handleRequestSort}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        />
      </PageContent>
    </PageContainer>
  );
};

export default TaxReportPdfDownload;
